// «Креативы → Библиотека» (этап 4b, DESIGN §6): поиск по смыслу одной строкой, сетка с превью, фильтры по виду,
// загрузка перетаскиванием. Gemini описывает видео по фрагментам — поиск находит нужный момент.
import { Button, Chip, EmptyState, IconButton, Meter, Skeleton, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, FolderOpen, Image as ImageIcon, Music, Search, Sparkles, Upload, X } from 'lucide-react';
import { type DragEvent, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { FilterBar, FilterSelect } from '../../filters';
import { SidePanel } from '../../shell/SidePanel';
import { PageHead } from '../common';
import { CreativeTabs } from './CreativeTabs';
import { mediaUrl, tc } from './data';
import './creatives.css';

interface AssetView {
  id: number;
  kind: string;
  kindLabel: string;
  title: string | null;
  description: string | null;
  tags: string[];
  origin: string;
  licence: string | null;
  sourceUrl: string | null;
  mime: string;
  bytes: number;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  path: string;
  poster: string | null;
  offerId: number | null;
  createdAt: string;
  segment?: { tStart: number; tEnd: number; description: string } | null;
  used?: number;
  bpm: number | null;
  /** Описание: нет, готовится, не получилось, есть. */
  describe?: 'none' | 'pending' | 'failed' | 'done';
}
interface LibraryData {
  items: AssetView[];
  counts: Record<string, number>;
  /** Файлов без описания — поиск по смыслу их не находит. */
  undescribed?: number;
}
interface AssetDetail extends Omit<AssetView, 'describe'> {
  segments: Array<{ tStart: number; tEnd: number; description: string }>;
  usedIn: Array<{ creativeId: number; code: string; slug: string; version: number }>;
  describe: { status: string; error: string | null } | null;
}

const KINDS: Array<{ key: string; label: string; match: string[] }> = [
  { key: 'all', label: 'Все', match: [] },
  { key: 'footage', label: 'Футаж', match: ['footage'] },
  { key: 'music', label: 'Музыка', match: ['music'] },
  { key: 'image', label: 'Картинки', match: ['image'] },
  { key: 'gen_video', label: 'Генерации видео', match: ['gen_video'] },
  { key: 'gen_image', label: 'Генерации картинок', match: ['gen_image'] },
];

const ORIGIN: Record<string, string> = {
  upload: 'загрузил владелец',
  telegram: 'из Telegram',
  hunter: 'нашёл агент',
  agent: 'добавил агент',
  fal: 'генерация',
  gemini: 'генерация',
  creatives_room: 'Creatives Room',
  buyo: 'кабинет buyo',
  import: 'импорт',
};

const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1)} МБ`;

const DESCRIBE_RU = {
  none: 'Без описания — поиск по смыслу его не найдёт',
  pending: 'Описание готовится…',
  failed: 'Описание не получилось',
  done: '',
} as const;

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

interface Uploading {
  name: string;
  progress: number;
  error?: string;
}

/** Загрузка потоком с прогрессом (XHR — у fetch нет прогресса отправки). */
export function uploadFile(
  file: File,
  onProgress: (p: number) => void,
  extra: Record<string, string> = {},
): Promise<AssetView & { created: boolean }> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', `/api/library/upload?${new URLSearchParams({ name: file.name, ...extra })}`);
    x.setRequestHeader('content-type', file.type || 'application/octet-stream');
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => {
      const data = (() => {
        try {
          return JSON.parse(x.responseText);
        } catch {
          return null;
        }
      })();
      if (x.status >= 200 && x.status < 300) resolve(data);
      else reject(new Error(data?.error ?? 'Не удалось загрузить'));
    };
    x.onerror = () => reject(new Error('Нет связи с платформой'));
    x.send(file);
  });
}

function AssetCard({ a, onOpen }: { a: AssetView; onOpen: () => void }) {
  const isMusic = a.kind === 'music' || a.kind === 'voice';
  return (
    <button type="button" className="creo lib-card" onClick={onOpen}>
      <div className={`thumb${isMusic ? ' thumb-audio' : ''}`}>
        {a.poster ? (
          <img src={mediaUrl(a.poster) ?? ''} alt="" loading="lazy" />
        ) : (
          <div className="thumb-empty">{isMusic ? <Music /> : <ImageIcon />}</div>
        )}
        <span className="thumb-st">
          <Chip>{a.kindLabel}</Chip>
        </span>
        {a.durationSec ? <span className="dur">{tc(a.durationSec)}</span> : null}
      </div>
      <div className="creo-b">
        <div className="creo-t">
          <span>{a.title ?? 'Без названия'}</span>
        </div>
        <div className="creo-a">
          {a.segment
            ? `${tc(a.segment.tStart)} — ${a.segment.description}`
            : (a.description ?? DESCRIBE_RU[a.describe ?? 'none'])}
        </div>
        <div className="creo-m">
          <span>{ORIGIN[a.origin] ?? a.origin}</span>
          {a.bpm ? <span className="tnum">{a.bpm} BPM</span> : null}
          {a.used ? <span>в {a.used} роликах</span> : null}
        </div>
      </div>
    </button>
  );
}

function AssetPanel({ id, onClose }: { id: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['library-asset', id],
    queryFn: () => api<AssetDetail>(`/api/library/${id}`),
    enabled: id !== null,
  });
  const a = q.data;
  const media = useRef<HTMLVideoElement & HTMLAudioElement>(null);
  const [licence, setLicence] = useState('');
  useEffect(() => setLicence(a?.licence ?? ''), [a?.licence]);
  const save = async (patch: Record<string, unknown>, msg: string) => {
    try {
      await api(`/api/library/${id}`, patch, 'PATCH');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['library'] }),
        qc.invalidateQueries({ queryKey: ['library-asset', id] }),
      ]);
      toast(msg);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const describe = async () => {
    try {
      await api(`/api/library/${id}/describe`, {});
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['library'] }),
        qc.invalidateQueries({ queryKey: ['library-asset', id] }),
      ]);
      toast('Описание готовится — обычно около минуты');
    } catch (e) {
      toast((e as Error).message);
    }
  };
  return (
    <SidePanel
      open={id !== null}
      label="Файл библиотеки"
      onClose={onClose}
      head={<div className="sp-title">{a?.title ?? 'Файл'}</div>}
    >
      {!a ? (
        <Skeleton h={320} />
      ) : (
        <div className="lib-detail">
          {a.mime.startsWith('video/') ? (
            // biome-ignore lint/a11y/useMediaCaption: футаж без речи; субтитров нет
            <video
              ref={media}
              className="lib-player"
              src={mediaUrl(a.path) ?? undefined}
              poster={mediaUrl(a.poster) ?? undefined}
              controls
              playsInline
              preload="none"
            />
          ) : a.mime.startsWith('audio/') ? (
            // biome-ignore lint/a11y/useMediaCaption: музыка без слов
            <audio ref={media} src={mediaUrl(a.path) ?? undefined} controls preload="none" />
          ) : (
            <img className="lib-player" src={mediaUrl(a.path) ?? ''} alt={a.title ?? ''} />
          )}
          {a.description ? (
            <p>{a.description}</p>
          ) : a.describe && !['failed', 'denied', 'cancelled'].includes(a.describe.status) ? (
            <p>Описание готовится…</p>
          ) : (
            <div className="lib-nodesc">
              <p>
                {a.describe?.error
                  ? `Описание не получилось: ${a.describe.error}`
                  : 'Без описания — поиск по смыслу этот файл не найдёт.'}
              </p>
              <Button size="sm" icon={<Sparkles />} onClick={() => void describe()}>
                {a.describe ? 'Описать ещё раз' : 'Описать'}
              </Button>
            </div>
          )}
          {a.tags.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {a.tags.map((t) => (
                <Chip key={t}>{t}</Chip>
              ))}
            </div>
          )}
          {a.segments.length > 0 && (
            <ul className="edits">
              {a.segments.map((s) => (
                <li key={s.tStart}>
                  <button
                    type="button"
                    className="edit-time mono"
                    onClick={() => {
                      if (media.current) {
                        media.current.currentTime = s.tStart;
                        void media.current.play().catch(() => {});
                      }
                    }}
                  >
                    {tc(s.tStart)}
                  </button>
                  <div className="edit-b">
                    <p>{s.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <dl className="facts">
            <div>
              <dt>Источник</dt>
              <dd>
                {ORIGIN[a.origin] ?? a.origin}
                {a.sourceUrl ? (
                  <small>
                    <a href={a.sourceUrl} target="_blank" rel="noreferrer">
                      {a.sourceUrl.slice(0, 60)}
                    </a>
                  </small>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>Файл</dt>
              <dd>
                {[
                  a.width && a.height ? `${a.width}×${a.height}` : null,
                  a.durationSec ? `${a.durationSec.toFixed(1)} с` : null,
                  a.bpm ? `${a.bpm} BPM` : null,
                  mb(a.bytes),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                <small>добавлен {when(a.createdAt)}</small>
              </dd>
            </div>
            {a.usedIn.length > 0 && (
              <div>
                <dt>В роликах</dt>
                <dd>
                  {a.usedIn.map((u) => (
                    <Link
                      key={`${u.creativeId}-${u.version}`}
                      to="/creatives/$id"
                      params={{ id: String(u.creativeId) }}
                    >
                      {u.slug}_{u.code} v{u.version}{' '}
                    </Link>
                  ))}
                </dd>
              </div>
            )}
          </dl>
          <form
            className="form-stack"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ licence }, 'Лицензия сохранена');
            }}
          >
            <label className="label" htmlFor="lib-licence">
              Лицензия и право на использование
            </label>
            <input
              id="lib-licence"
              className="input"
              value={licence}
              placeholder="свой футаж, видео поставщика, Pixabay License…"
              onChange={(e) => setLicence(e.target.value)}
            />
            <div className="flex gap-2 flex-wrap">
              <Button type="submit" size="sm" disabled={licence === (a.licence ?? '')}>
                Сохранить
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon={<Archive />}
                onClick={() => void save({ archived: true }, 'Убрано в архив')}
              >
                В архив
              </Button>
            </div>
          </form>
        </div>
      )}
    </SidePanel>
  );
}

export function LibraryPage() {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [kind, setKind] = useState('all');
  const [open, setOpen] = useState<number | null>(null);
  const [drag, setDrag] = useState(false);
  const [uploads, setUploads] = useState<Uploading[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const q = useDebounced(text.trim(), 400);
  const lib = useQuery({
    queryKey: ['library', q, kind],
    queryFn: () =>
      api<LibraryData>(`/api/library?${new URLSearchParams({ ...(q ? { q } : {}), kind }).toString()}`),
    placeholderData: (prev) => prev,
  });

  const upload = async (files: FileList | File[]) => {
    const list = [...files].filter((f) => /^(video|audio|image)\//.test(f.type));
    if (!list.length) return toast('Можно загружать видео, аудио и картинки');
    for (const f of list) {
      setUploads((u) => [...u, { name: f.name, progress: 0 }]);
      try {
        const r = await uploadFile(f, (p) =>
          setUploads((u) => u.map((x) => (x.name === f.name ? { ...x, progress: p } : x))),
        );
        setUploads((u) => u.filter((x) => x.name !== f.name));
        toast(
          r.created
            ? `«${f.name}» в библиотеке — описание будет через минуту`
            : `«${f.name}» уже был в библиотеке`,
        );
      } catch (e) {
        setUploads((u) => u.map((x) => (x.name === f.name ? { ...x, error: (e as Error).message } : x)));
      }
    }
    await qc.invalidateQueries({ queryKey: ['library'] });
  };

  const [describingAll, setDescribingAll] = useState(false);
  const describeAll = async () => {
    setDescribingAll(true);
    try {
      const r = await api<{ queued: number; stopped: string | null }>('/api/library/describe-missing', {});
      toast(
        r.stopped
          ? `Поставлено: ${r.queued}. Остановились: ${r.stopped}`
          : `Поставлено описывать: ${r.queued} — описания появятся в течение нескольких минут`,
      );
      await qc.invalidateQueries({ queryKey: ['library'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setDescribingAll(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };

  const counts = lib.data?.counts ?? {};
  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: зона перетаскивания файлов — на всю страницу
    <div
      className={`lib-drop${drag ? ' on' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDrag(false)}
      onDrop={onDrop}
    >
      <PageHead
        title="Креативы"
        sub={`Библиотека · ${total} ${total === 1 ? 'файл' : 'файлов'}: футаж, музыка, картинки и генерации`}
        actions={
          <>
            <input
              ref={input}
              type="file"
              multiple
              accept="video/*,audio/*,image/*"
              hidden
              onChange={(e) => {
                if (e.target.files) void upload(e.target.files);
                e.target.value = '';
              }}
            />
            <Button variant="primary" icon={<Upload />} onClick={() => input.current?.click()}>
              Загрузить файлы
            </Button>
          </>
        }
      />
      <CreativeTabs active="/creatives/library" />
      <div className="pc">
        {uploads.length > 0 && (
          <ul className="uploads">
            {uploads.map((u) => (
              <li key={u.name}>
                <span>{u.name}</span>
                {u.error ? (
                  <>
                    <span className="neg">{u.error}</span>
                    <IconButton
                      label="Убрать"
                      onClick={() => setUploads((x) => x.filter((y) => y.name !== u.name))}
                    >
                      <X />
                    </IconButton>
                  </>
                ) : (
                  <Meter value={u.progress} label={`Загрузка ${u.name}`} />
                )}
              </li>
            ))}
          </ul>
        )}
        {(lib.data?.undescribed ?? 0) > 0 && (
          <div className="notice info lib-undesc">
            <Sparkles aria-hidden />
            <p>
              Без описания: <b className="tnum">{lib.data?.undescribed}</b> — поиск по смыслу их не находит.
              Описывает Gemini в пределах месячного бюджета библиотеки (настройки генераций).
            </p>
            <Button size="sm" disabled={describingAll} onClick={() => void describeAll()}>
              {describingAll ? 'Ставлю в очередь…' : 'Описать все'}
            </Button>
          </div>
        )}
        <FilterBar
          search={
            <label className="search-field filter-end lib-search">
              <Search aria-hidden />
              <input
                type="search"
                placeholder="Поиск по смыслу: «рука приклеивает ленту», «кухня утром», «энергичный бит»"
                aria-label="Поиск по смыслу"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>
          }
        >
          <FilterSelect
            label="Вид"
            value={kind}
            onChange={setKind}
            allValue="all"
            options={KINDS.map((k) => ({
              value: k.key,
              label: k.label,
              count: k.key === 'all' ? total : k.match.reduce((s, m) => s + (counts[m] ?? 0), 0),
            }))}
          />
        </FilterBar>
        {lib.isPending ? (
          <div className="cgrid">
            {Array.from({ length: 8 }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: скелетоны без данных
              <Skeleton key={i} className="thumb" h={0} />
            ))}
          </div>
        ) : lib.isError ? (
          <div className="card">
            <EmptyState
              icon={<FolderOpen />}
              title="Не удалось загрузить библиотеку"
              action={<Button onClick={() => lib.refetch()}>Повторить</Button>}
            >
              {(lib.error as Error).message}
            </EmptyState>
          </div>
        ) : !lib.data?.items.length ? (
          <div className="card">
            <EmptyState
              icon={<FolderOpen />}
              title={q ? 'Ничего не нашлось' : 'Библиотека пуста'}
              action={
                q ? undefined : (
                  <Button variant="primary" icon={<Upload />} onClick={() => input.current?.click()}>
                    Загрузить футаж
                  </Button>
                )
              }
            >
              {q
                ? 'Попробуйте другими словами — поиск понимает смысл, а не только точные слова.'
                : 'Перетащите сюда свой футаж, музыку или фото товара. Агент тоже складывает сюда найденное и удачные генерации.'}
            </EmptyState>
          </div>
        ) : (
          <div className="cgrid">
            {lib.data.items.map((a) => (
              <AssetCard key={a.id} a={a} onOpen={() => setOpen(a.id)} />
            ))}
          </div>
        )}
      </div>
      <AssetPanel id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
